import React, {useContext, useEffect, useMemo, useState} from "react";
import "./BlogPublic.scss";
import BlogCard from "../../components/blogCard/BlogCard";
import BlogDetailModal from "../../components/blogDetailModal/BlogDetailModal";
import {blogSection} from "../../portfolio";
import {Fade} from "react-reveal";
import StyleContext from "../../contexts/StyleContext";
import {commentOnBlogPost, fetchPublishedBlogs, likeBlogPost} from "../../utils/blogApi";
import {sortPostsByDate} from "../../utils/blogs";

export default function Blogs() {
  const {isDark} = useContext(StyleContext);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedPost, setSelectedPost] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadPosts() {
      setIsLoading(true);
      setLoadError("");
      try {
        const response = await fetchPublishedBlogs();
        if (!isMounted) {
          return;
        }

        setPosts(sortPostsByDate(response.posts || []));
      } catch (error) {
        if (isMounted) {
          setLoadError(error.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadPosts();

    return () => {
      isMounted = false;
    };
  }, []);

  const visiblePosts = useMemo(() => {
    return posts.filter(post => {
      if (sourceFilter === "all") {
        return true;
      }

      return post.source === sourceFilter;
    });
  }, [posts, sourceFilter]);

  const featuredPost = visiblePosts[0] || null;
  const remainingPosts = featuredPost
    ? visiblePosts.filter(post => post.id !== featuredPost.id)
    : visiblePosts;

  if (!blogSection.display) {
    return null;
  }

  function updatePostInState(updatedPost) {
    setPosts(previousPosts => {
      const nextPosts = sortPostsByDate(
        previousPosts.map(post => (post.id === updatedPost.id ? updatedPost : post))
      );
      return nextPosts;
    });
    setSelectedPost(updatedPost);
  }

  async function toggleLike(postId) {
    setIsSubmitting(true);
    try {
      const response = await likeBlogPost(postId);
      updatePostInState(response.post);
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function addComment(postId, comment) {
    setIsSubmitting(true);
    try {
      const response = await commentOnBlogPost(postId, comment);
      updatePostInState(response.post);
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Fade bottom duration={1000} distance="20px">
      <div className="main blog-shell" id="blogs">
        <div className="blog-header">
          <div className="blog-kicker">Writing</div>
          <div className="blog-heading-row">
            <div>
              <h1 className="blog-header-text">{blogSection.title}</h1>
              <p
                className={
                  isDark ? "dark-mode blog-subtitle" : "subTitle blog-subtitle"
                }
              >
                {blogSection.subtitle}
              </p>
            </div>
            <div className={isDark ? "blog-stats blog-stats-dark" : "blog-stats"}>
              <div>
                <strong>{posts.filter(post => post.source === "custom").length}</strong>
                <span>portfolio notes</span>
              </div>
              <div>
                <strong>{posts.filter(post => post.source === "medium").length}</strong>
                <span>Medium posts</span>
              </div>
              <div>
                <strong>{posts.reduce((total, post) => total + (post.likesCount || 0), 0)}</strong>
                <span>likes</span>
              </div>
            </div>
          </div>
          {blogSection.intro ? <p className="blog-intro-copy">{blogSection.intro}</p> : null}
          {loadError ? <div className="blog-load-error">{loadError}</div> : null}
        </div>

        <div className="blog-filter-row">
          {[
            {id: "all", label: "All writing"},
            {id: "custom", label: "Portfolio notes"},
            {id: "medium", label: "Medium"}
          ].map(filter => (
            <button
              className={sourceFilter === filter.id ? "blog-filter blog-filter-active" : "blog-filter"}
              key={filter.id}
              onClick={() => setSourceFilter(filter.id)}
              type="button"
            >
              {filter.label}
            </button>
          ))}
        </div>

        {isLoading ? <div className="blog-empty-state">Loading writing...</div> : null}

        {!isLoading && featuredPost ? (
          <div className="blog-featured-layout">
            <article className={isDark ? "blog-featured-card blog-featured-card-dark" : "blog-featured-card"}>
              <div className="blog-featured-copy">
                <div className="blog-card-source">
                  {featuredPost.source === "medium" ? "Latest from Medium" : "Featured portfolio note"}
                </div>
                <h2>{featuredPost.title}</h2>
                <p>{featuredPost.excerpt}</p>
                <div className="blog-tag-row">
                  {(featuredPost.tags || []).slice(0, 4).map(tag => (
                    <span className="blog-tag" key={`${featuredPost.id}-${tag}`}>
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="blog-featured-actions">
                  <button className="blog-editor-button" onClick={() => setSelectedPost(featuredPost)} type="button">
                    Read article
                  </button>
                  <div className="blog-card-stats">
                    <span className="blog-card-engagement">{featuredPost.likesCount || 0} likes</span>
                    <span className="blog-card-engagement">{featuredPost.commentCount || 0} comments</span>
                  </div>
                </div>
              </div>
              {featuredPost.coverImage ? (
                <div className="blog-featured-image">
                  <img src={featuredPost.coverImage} alt={featuredPost.title} />
                </div>
              ) : null}
            </article>

            <div className="blog-text-div">
              {remainingPosts.map(blog => (
                <BlogCard key={blog.id} blog={blog} onSelectPost={setSelectedPost} />
              ))}
            </div>
          </div>
        ) : null}

        {!isLoading && !visiblePosts.length ? (
          <div className={isDark ? "blog-empty-state blog-empty-state-dark" : "blog-empty-state"}>
            <h3>No published writing yet.</h3>
            <p>Open the dedicated admin route to draft and publish your first post.</p>
          </div>
        ) : null}

        {selectedPost ? (
          <BlogDetailModal
            isDark={isDark}
            isSubmitting={isSubmitting}
            onAddComment={addComment}
            onClose={() => setSelectedPost(null)}
            onToggleLike={toggleLike}
            post={selectedPost}
          />
        ) : null}
      </div>
    </Fade>
  );
}
